import {
  operationHasDocumentAssignment,
  isInternationalStatementAmount,
} from './tcStatementHelpers'
import { cct } from 'src/utils/creditCardsI18n'

export const TC_OBLIGATION_INTERNATIONAL_TYPE = 'tarjetadecreditointernacional'

export const isInternationalTcObligation = (doc) =>
  String(doc?.meta?.type || '')
    .trim()
    .toLowerCase() === TC_OBLIGATION_INTERNATIONAL_TYPE

/** Monto a mostrar en listados/detalle: TC internacional siempre CLP (meta.amount). */
export const getObligationDisplayAmount = (doc) => {
  if (!doc) return 0
  if (isInternationalTcObligation(doc)) {
    return Number(doc.meta?.amount ?? doc.amount ?? 0) || 0
  }
  const meta = doc.meta || {}
  if ([0, '0', 1, '1'].includes(meta.documentCode) && meta.netAmount != null) {
    return Number(meta.netAmount) || 0
  }
  return Number(meta.originalAmount ?? meta.amount ?? doc.amount ?? meta.netAmount ?? 0) || 0
}

const filterUnassigned = (ops) =>
  (Array.isArray(ops) ? ops : []).flatMap((op) => {
    if (op.section === '4') return []
    const difference = (op.allocations || []).find((allocation) => allocation.type === 'DIFFERENCE')
    if (difference) {
      return [
        {
          ...op,
          description: difference.description || 'DIFERENCIA',
          installmentLocalAmount: difference.amountLocal,
          installmentClpAmount: difference.amountClp,
          accountKey: difference.accountKey,
          accountName: difference.accountName,
          allocationId: difference.allocationId,
        },
      ]
    }
    return operationHasDocumentAssignment(op) ? [] : [op]
  })

/** Amount committed to the invoice by one op (INVOICE allocation if split, else installment). */
export const getOperationInvoiceCommitmentAmount = (op, options = {}) => {
  const useClp = options.useClpAmount === true
  const invoiceAllocation = (op?.allocations || []).find((a) => a.type === 'INVOICE')
  if (invoiceAllocation) {
    if (useClp) {
      const clp = Math.round(Math.abs(Number(invoiceAllocation.amountClp) || 0))
      if (clp > 0) return clp
    }
    return Math.round(Math.abs(Number(invoiceAllocation.amountLocal) || 0))
  }
  if (useClp) {
    const clp = Math.round(Math.abs(Number(op?.installmentClpAmount) || 0))
    if (clp > 0) return clp
  }
  return Math.round(Math.abs(Number(op?.installmentLocalAmount) || 0))
}

export const sumStatementCommitmentsToInvoice = (ops, invoiceId, excludeIndex, options = {}) => {
  const targetId = String(invoiceId || '').trim()
  if (!targetId) return 0
  let sum = 0
  ;(Array.isArray(ops) ? ops : []).forEach((op, i) => {
    if (excludeIndex != null && i === excludeIndex) return
    if (String(op?.associatedDocumentId || '').trim() !== targetId) return
    sum += getOperationInvoiceCommitmentAmount(op, options)
  })
  return sum
}

export const getEffectiveInvoiceRemainingForStatement = ({
  invoiceRemaining,
  ops,
  invoiceId,
  excludeIndex,
  useClpAmount,
}) => {
  const remaining = Math.round(Math.abs(Number(invoiceRemaining) || 0))
  const committed = sumStatementCommitmentsToInvoice(ops, invoiceId, excludeIndex, {
    useClpAmount,
  })
  return Math.max(0, remaining - committed)
}

export const INVOICE_PARTIAL_REMAINING_ASSIGN_MESSAGE =
  cct('validation.invoicePartiallyMatched')

export const INVOICE_ZERO_WITHOUT_TC_ASSIGN_MESSAGE =
  cct('validation.invoiceNoRemainingNotMatched')

export const INVOICE_TC_OBLIGATION_NO_CUPO_MESSAGE =
  cct('validation.obligationNoCapacity')

export const getInvoiceFaceAmountFromCandidate = (doc) => {
  const meta = doc?.meta || {}
  const raw =
    meta.originalAmount != null
      ? Number(meta.originalAmount)
      : meta.amount != null
        ? Number(meta.amount)
        : Number(doc?.amount ?? 0)
  return Math.round(Math.abs(raw) || 0)
}

/**
 * Client-side pre-check. Partial invoices are blocked; zero remaining defers to backend
 * (may still be assignable if linked to a TC obligation with cupo).
 */
export const classifyInvoiceAssignmentScenarioClient = ({
  invoiceRemaining,
  invoiceFaceAmount,
}) => {
  const remaining = Math.round(Math.abs(Number(invoiceRemaining) || 0))
  const face = Math.round(Math.abs(Number(invoiceFaceAmount) || 0))
  if (remaining <= 0) return 'ZERO'
  if (face > 0 && remaining < face) return 'PARTIAL'
  return 'FULL_OPEN'
}

/** Ops with factura assigned but no TC obligation link yet (deferred to Paso 3). */
export const hasPendingInvoiceTcLinks = (ops) =>
  (Array.isArray(ops) ? ops : []).some((op) => {
    if (!String(op?.associatedDocumentId || '').trim()) return false
    const invoiceAllocation = (op.allocations || []).find((a) => a.type === 'INVOICE')
    const linked = String(
      invoiceAllocation?.linkedTcObligationDocumentId || op.linkedTcObligationDocumentId || '',
    ).trim()
    return !linked
  })

export const isStatementReconciliationLocked = (file) => {
  if (!file) return false
  if (file.isLocked === true) return true
  if (file.isLocked === false) return false
  return file.creditCardPaso3Completed === true
}

export const statementHasCreditCardObligation = (file) =>
  Boolean(String(file?.creditCardObligationDocumentId || '').trim()) ||
  file?.creditCardPaso3Completed === true

/** Assignments stay locked after Paso 3 unless the statement is unlocked and in modification. */
export const isStatementActionsLocked = (file) =>
  isStatementReconciliationLocked(file) ||
  (statementHasCreditCardObligation(file) && file?.creditCardInModification !== true)

/** A non-section-4 op is covered for re-Paso 3 if it has invoice, account, or period TC inheritance. */
export const operationHasPaso3Coverage = (op, periodObligationId) => {
  if (!op || op.section === '4') return true
  if (String(op.associatedDocumentId || '').trim()) return true
  if (String(op.accountKey || '').trim()) return true
  const difference = (op.allocations || []).find((allocation) => allocation.type === 'DIFFERENCE')
  if (String(difference?.accountKey || '').trim()) return true
  return Boolean(String(periodObligationId || '').trim()) && op.suppressPeriodTcObligation !== true
}

export const statementOperationsHavePaso3Coverage = (ops, periodObligationId) =>
  (Array.isArray(ops) ? ops : []).every((op) => operationHasPaso3Coverage(op, periodObligationId))

/** Period list badge only — Rendido requires creditCardPaso3Completed (no legacy doc-id fallback). */
export const PERIOD_STATEMENT_UI_STATUS = {
  EMPTY: 'EMPTY',
  LOADED: 'LOADED',
  RENDERED: 'RENDERED',
  IN_MODIFICATION: 'IN_MODIFICATION',
}

export const getPeriodStatementUiStatus = (files) => {
  const list = Array.isArray(files) ? files : []
  if (list.length === 0) return PERIOD_STATEMENT_UI_STATUS.EMPTY
  if (list.some((file) => file?.creditCardInModification === true)) {
    return PERIOD_STATEMENT_UI_STATUS.IN_MODIFICATION
  }
  const allRendered = list.every((file) => file?.creditCardPaso3Completed === true)
  return allRendered ? PERIOD_STATEMENT_UI_STATUS.RENDERED : PERIOD_STATEMENT_UI_STATUS.LOADED
}

export const getPeriodStatementStatusLabel = (status) => {
  if (status === PERIOD_STATEMENT_UI_STATUS.IN_MODIFICATION) return cct('page.statusInModification')
  if (status === PERIOD_STATEMENT_UI_STATUS.RENDERED) return cct('page.statusRendered')
  if (status === PERIOD_STATEMENT_UI_STATUS.LOADED) return cct('page.statusLoaded')
  return cct('page.statusEmpty')
}

/** Suma Monto mes de operaciones sin factura/invoice; excluye sección 4. */
export const sumUnassignedInstallments = (ops, options = {}) => {
  const { statementAmountCurrency } = options
  const unassigned = filterUnassigned(ops)

  if (isInternationalStatementAmount(statementAmountCurrency)) {
    return unassigned.reduce((acc, op) => {
      const clp = Number(op.installmentClpAmount)
      return acc + (Number.isFinite(clp) ? clp : 0)
    }, 0)
  }

  return unassigned.reduce((acc, op) => acc + (Number(op.installmentLocalAmount) || 0), 0)
}

export const hasUnassignedInstallmentsWithClp = (ops, statementAmountCurrency) => {
  if (!isInternationalStatementAmount(statementAmountCurrency)) {
    return sumUnassignedInstallments(ops) > 0
  }
  const unassigned = filterUnassigned(ops)
  if (unassigned.length === 0) return false
  return unassigned.every((op) => Number(op.installmentClpAmount) > 0)
}

export const countUnassignedOperations = (ops) => filterUnassigned(ops).length

/** Unassigned operations (rendición set) without accountKey. */
export const unassignedOpsMissingAccountKey = (ops) =>
  filterUnassigned(ops).filter((op) => !String(op?.accountKey || '').trim())
